import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import MarkdownCell from 'components/upb_viewer_beta/copilot/components/MarkdownCell';
import PlainButton from 'components/upb_viewer_beta/copilot/components/PlainButton';
import {
  ACCENT_VIVID,
  BG_1,
  BG_3,
  BORDER_1,
  BORDER_2,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_SIZE_XXS,
  FONT_WEIGHT_SEMIBOLD,
  RADIUS_SM,
  SPACE_2,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * Markdown — a tiny, safe renderer for Marco's answer prose.
 *
 * The agent writes light markdown (**bold**, `code`, "- " bullet lists, blank-line paragraphs,
 * and ```fenced``` code blocks). We build React nodes rather than setting innerHTML, so echoed
 * product text can never inject markup. Fenced blocks get Copy + Download controls — a safety net
 * so any tabular/CSV text the agent emits is always one click from a file, even if it didn't use
 * a DataTable.
 */

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Inline: split on **bold** and `code`; everything else is literal text.
const INLINE_RE = /(\*\*[\s\S]+?\*\*|`[^`]+`)/g;

function renderInline(text, keyPrefix) {
  const parts = text.split(INLINE_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={key} className={css(styles.strong)}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={key} className={css(styles.code)}>
          {part.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

const isBullet = line => /^\s*[-*]\s+/.test(line);
const isFence = line => /^\s*```/.test(line);
const fenceLang = line =>
  line
    .replace(/^\s*```/, '')
    .trim()
    .toLowerCase();

const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const isHr = line => /^\s*([-*_])\1{2,}\s*$/.test(line); // ---, ***, ___ on their own line
const isTableRow = line => line.includes('|') && !isFence(line);

// Split a "| a | b |" row into trimmed cells, dropping the outer pipes.
const parseRow = line => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(c => c.trim());
};
// The "|---|:--:|" line that separates a markdown table header from its body.
const isSepRow = line => {
  const cells = parseRow(line);
  return cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));
};

const looksCsv = content => {
  const rows = content.trim().split(/\r?\n/);
  return rows.length >= 2 && rows.every(r => r.includes(',') || r.includes('\t'));
};

function download(content, filename, mime, bom) {
  const blob = new Blob([bom ? `\ufeff${content}` : content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CodeBlock({ lang, content }) {
  const [copied, setCopied] = useState(false);
  const csv = lang === 'csv' || (lang !== 'json' && looksCsv(content));
  const label = lang || (csv ? 'csv' : 'text');

  const onCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        },
        () => {},
      );
    }
  };
  const onDownload = () =>
    csv
      ? download(content, 'marco-data.csv', 'text/csv', true)
      : download(content, `marco-snippet.${lang === 'json' ? 'json' : 'txt'}`, 'text/plain', false);

  return (
    <div className={css(styles.codeBlock)}>
      <div className={css(styles.codeBar)}>
        <span className={css(styles.codeLang)}>{label}</span>
        <div className={css(styles.codeActions)}>
          <PlainButton className={css(styles.codeBtn)} onClick={onCopy}>
            {copied ? 'Copied' : 'Copy'}
          </PlainButton>
          <PlainButton className={css(styles.codeBtn)} onClick={onDownload}>
            Download
          </PlainButton>
        </div>
      </div>
      <pre className={css(styles.pre)}>{content}</pre>
    </div>
  );
}
CodeBlock.propTypes = { lang: PropTypes.string, content: PropTypes.string };
CodeBlock.defaultProps = { lang: '', content: '' };

function Table({ rows, kp }) {
  const header = parseRow(rows[0]);
  const body = rows.slice(2).map(parseRow);
  return (
    <div className={css(styles.tableWrap)}>
      <table className={css(styles.table)}>
        <thead>
          <tr>
            {header.map((h, i) => (
              <MarkdownCell key={`${kp}-th-${i}`} tag="th" className={css(styles.th)}>
                {renderInline(h, `${kp}-th-${i}`)}
              </MarkdownCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={ri}>
              {header.map((_, ci) => (
                <MarkdownCell key={`${kp}-td-${ri}-${ci}`} tag="td" className={css(styles.td)}>
                  {renderInline(row[ci] || '', `${kp}-td-${ri}-${ci}`)}
                </MarkdownCell>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
Table.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.string).isRequired,
  kp: PropTypes.string.isRequired,
};

const HEADING_SIZE = { 1: 17, 2: 15, 3: 14 };

export default function Markdown({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let bullets = null; // open <ul> buffer
  let code = null; // open code-fence buffer: { lang, body: [] }
  let table = null; // open pipe-table buffer: array of raw row strings

  const flushBullets = () => {
    if (!bullets) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className={css(styles.ul)}>
        {bullets.map((b, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className={css(styles.li)}>
            {renderInline(b, `li-${blocks.length}-${i}`)}
          </li>
        ))}
      </ul>,
    );
    bullets = null;
  };

  const flushCode = () => {
    if (!code) return;
    // `marco-ui` is Marco's internal component contract, never meant for the user. The backend
    // normally strips it; if a truncated/leaked block slips through, drop it (don't show raw JSON).
    if (!/^marco[-_]?ui$/.test(code.lang)) {
      blocks.push(
        <CodeBlock key={`code-${blocks.length}`} lang={code.lang} content={code.body.join('\n')} />,
      );
    }
    code = null;
  };

  // A valid table needs a header row + a "|---|" separator; otherwise the buffered lines were
  // just prose that happened to contain pipes, so render them as paragraphs.
  const flushTable = () => {
    if (!table) return;
    if (table.length >= 2 && isSepRow(table[1])) {
      blocks.push(<Table key={`tbl-${blocks.length}`} rows={table} kp={`tbl-${blocks.length}`} />);
    } else {
      table.forEach((ln, i) =>
        blocks.push(
          <p key={`pt-${blocks.length}-${i}`} className={css(styles.p)}>
            {renderInline(ln, `pt-${blocks.length}-${i}`)}
          </p>,
        ),
      );
    }
    table = null;
  };

  lines.forEach((line, idx) => {
    if (isFence(line)) {
      if (code) {
        flushCode(); // closing fence
      } else {
        flushBullets();
        flushTable();
        code = { lang: fenceLang(line), body: [] };
      }
      return;
    }
    if (code) {
      code.body.push(line);
      return;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      flushBullets();
      flushTable();
      const level = heading[1].length;
      blocks.push(
        <div
          key={`h-${idx}`}
          className={css(styles.heading)}
          style={{ fontSize: HEADING_SIZE[level] || 13 }}
        >
          {renderInline(heading[2], `h-${idx}`)}
        </div>,
      );
      return;
    }
    if (isHr(line)) {
      flushBullets();
      flushTable();
      blocks.push(<hr key={`hr-${idx}`} className={css(styles.hr)} />);
      return;
    }
    if (isBullet(line)) {
      flushTable();
      if (!bullets) bullets = [];
      bullets.push(line.replace(/^\s*[-*]\s+/, ''));
      return;
    }
    if (isTableRow(line)) {
      flushBullets();
      if (!table) table = [];
      table.push(line);
      return;
    }

    flushBullets();
    flushTable();
    if (line.trim() === '') return; // blank line = block separator
    blocks.push(
      <p key={`p-${idx}`} className={css(styles.p)}>
        {renderInline(line, `p-${idx}`)}
      </p>,
    );
  });
  flushBullets();
  flushTable();
  flushCode(); // render an unterminated block too (e.g. mid-stream)

  return <>{blocks}</>;
}

Markdown.propTypes = { text: PropTypes.string };
Markdown.defaultProps = { text: '' };

const styles = StyleSheet.create({
  p: { margin: 0, ':not(:last-child)': { marginBottom: SPACE_2 } },
  strong: { fontWeight: FONT_WEIGHT_SEMIBOLD },
  ul: { margin: 0, paddingLeft: 18, ':not(:last-child)': { marginBottom: SPACE_2 } },
  li: { marginBottom: 2 },
  heading: {
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    lineHeight: 1.3,
    marginTop: SPACE_2,
    ':not(:last-child)': { marginBottom: SPACE_2 },
  },
  hr: {
    border: 'none',
    borderTop: `1px solid ${BORDER_1}`,
    margin: `${SPACE_2}px 0`,
  },
  tableWrap: {
    overflowX: 'auto',
    ':not(:last-child)': { marginBottom: SPACE_2 },
  },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: '0.92em' },
  th: {
    textAlign: 'left',
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
    padding: '5px 8px',
    borderBottom: `1px solid ${BORDER_2}`,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '5px 8px',
    borderBottom: `1px solid ${BORDER_1}`,
    verticalAlign: 'top',
  },
  code: {
    background: BG_3,
    color: FG_2,
    borderRadius: 4,
    padding: '1px 5px',
    fontFamily: MONO,
    fontSize: '0.92em',
  },
  codeBlock: {
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_SM,
    overflow: 'hidden',
    ':not(:last-child)': { marginBottom: SPACE_2 },
  },
  codeBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    background: BG_3,
    borderBottom: `1px solid ${BORDER_1}`,
  },
  codeLang: {
    fontFamily: MONO,
    fontSize: FONT_SIZE_XXS,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: FG_3,
  },
  codeActions: { display: 'flex', gap: 4 },
  codeBtn: {
    background: 'transparent',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_SM,
    color: FG_2,
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    padding: '2px 9px',
    transition: 'color 0.15s, border-color 0.15s, background 0.15s',
    ':hover': { color: ACCENT_VIVID, borderColor: ACCENT_VIVID, background: BG_1 },
  },
  pre: {
    margin: 0,
    padding: SPACE_2,
    background: BG_1,
    color: FG_2,
    fontFamily: MONO,
    fontSize: '0.86em',
    lineHeight: 1.5,
    overflowX: 'auto',
    whiteSpace: 'pre',
  },
});
